import { useTranslate } from '@tolgee/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { ArrowLeftSvg } from '../../../assets/icons';
import { ArrowRightSvg } from '../../../assets/icons/ArrowRightSvg';
import { ChatBubbleSvg } from '../../../assets/icons/ChatBubbleSvg';
import { CheckSvg } from '../../../assets/icons/CheckSvg';
import { CrossSvg } from '../../../assets/icons/CrossSvg';
import theme from '../../../assets/theme';
import usePoll from '../../../hooks/usePoll';
import {
  useRegisterClickerHandlers,
  useRegisterClickerLiveState,
} from '../../../providers/LiveLessonClickerProvider/LiveLessonClickerProvider';
import {
  hasMcExplanationBeenOpened as readMcExplanationOpened,
  setMcExplanationOpened,
} from '../../../utils/liveGameStateStorage';
import { parseStringToSlateObject } from '../../SlideComponents/slateUtil';
import { PollOptions } from '../../SlideComponents/slide.types';
import { LatexRenderer } from '../DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';
import { letterFor } from './letterFor';

// ── Animation constants ───────────────────────────────────────────────────
// Mirrors AiFeedbackScoreboard's reveal cadence so the live game has one
// shared visual language across activities.
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BAR_FILL_MS = 750;
const ROW_IN_MS = 400;

type RevealOption = {
  id: string;
  displayNameRaw: string;
  isCorrect: boolean;
  explanationRaw: string;
  count: number;
  // First non-empty leaf color we can find in the displayName slate JSON.
  // Used to tint the count number above the bar so each option's count
  // matches its display-name color. Falls back to undefined when nothing
  // is authored — Count's default text color then applies.
  displayColor?: string;
};

// Pull the first leaf color out of a slate JSON string. The slate doc has
// the shape `[{ type:'paragraph', children:[{ text, color?, … }] }]`; we
// walk paragraphs in order and return the first non-empty `color`. Falls
// back to the paragraph-level `color` if no leaf carries one.
const extractSlateColor = (raw: string): string | undefined => {
  if (!raw) return undefined;
  try {
    const obj = parseStringToSlateObject(raw) as {
      color?: string;
      children?: { color?: string }[];
    }[];
    for (const block of obj) {
      const leafColor = block.children?.find(c => !!c.color)?.color;
      if (leafColor) return leafColor;
      if (block.color) return block.color;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

type Props = {
  gameId?: string;
  roundId?: string;
  isAuthenticated: boolean;
  tenantId?: string;
  /**
   * Fallback options used before live poll data arrives. While `data` from
   * `usePoll` is undefined we render an anticipating state using just the
   * option text — no counts, no correctness, no explanation pill.
   */
  staticPollOptions?: PollOptions[];
};

const MultipleChoiceHostReveal = ({
  gameId,
  roundId,
  isAuthenticated,
  staticPollOptions,
  tenantId,
}: Props) => {
  const { t } = useTranslate();
  const { data } = usePoll({ gameId, roundId, isAuthenticated, tenantId });
  const [openOptionId, setOpenOptionId] = useState<string | null>(null);
  // Latches once the host has stepped into the explanation flow on this
  // slide. Persisted via liveGameStateStorage so navigating back to a
  // previously-walked slide doesn't re-open the modal. The local copy
  // (state) keeps decideNextAction reactive to the latch; the storage
  // value is the source of truth on mount / slide change.
  const [hasMcExplanationBeenOpened, setHasMcExplanationBeenOpenedState] = useState(() =>
    gameId && roundId ? readMcExplanationOpened(gameId, roundId) : false,
  );
  // Re-hydrate from storage on slide change (or game change). Also
  // resets the open-modal state so a stale openOptionId doesn't bleed
  // across rounds.
  useEffect(() => {
    const stored = gameId && roundId ? readMcExplanationOpened(gameId, roundId) : false;
    setHasMcExplanationBeenOpenedState(stored);
    setOpenOptionId(null);
  }, [gameId, roundId]);
  // Setter that also writes to storage. Keeps the React state and the
  // persistent flag in sync.
  const setHasMcExplanationBeenOpened = useCallback(() => {
    setHasMcExplanationBeenOpenedState(true);
    if (gameId && roundId) {
      setMcExplanationOpened(gameId, roundId);
    }
  }, [gameId, roundId]);

  // Once `usePoll` has data, we've "received responses" and we can reveal
  // counts + correctness + explanation pills. Until then we hold the
  // anticipating state, showing only the option text.
  const hasResults = !!data && data.length > 0;

  const options: RevealOption[] = useMemo(() => {
    // `staticPollOptions` carries the teacher-authored fields
    // (`isCorrect`, `explanation`, `displayName`, color order). The poll
    // endpoint is shared with regular polls and may not surface
    // isCorrect/explanation for multipleChoice rounds — so we use the
    // authored options as the spine and merge live `count` values in by id
    // when data arrives. If for some reason a staticPollOptions entry is
    // missing, we still fall back to any data-only entries so the chart at
    // least renders something.
    const authoredById = new Map((staticPollOptions || []).map(o => [o.id, o] as const));
    const countById = new Map(
      (data || []).map(o => [o.id, typeof o.count === 'number' ? o.count : 0]),
    );

    const ordered = staticPollOptions?.length
      ? staticPollOptions.map(o => o.id)
      : (data || []).map(o => o.id);

    return ordered.map(id => {
      const authored = authoredById.get(id);
      const live = (data || []).find(d => d.id === id);
      // Prefer authored metadata; fall back to live response for anything
      // the authored option didn't carry (paranoia for malformed fixtures).
      const displayNameRaw = authored?.displayName || live?.displayName || '';
      return {
        id,
        displayNameRaw,
        isCorrect: !!(authored?.isCorrect ?? live?.isCorrect),
        explanationRaw: authored?.explanation || live?.explanation || '',
        count: countById.get(id) ?? 0,
        displayColor: extractSlateColor(displayNameRaw),
      };
    });
  }, [data, staticPollOptions]);

  // Map each option id to a stable letter (A, B, C, …) based on the *authored*
  // order. The chart sorts by count, but the letter belongs to the option, not
  // its rank — so an option keeps its letter regardless of how votes shake the
  // bars. We anchor on `staticPollOptions` when available (authored order) and
  // fall back to whatever order the poll endpoint returned.
  const letterByOptionId = useMemo(() => {
    const ordered = staticPollOptions?.length
      ? staticPollOptions.map(o => o.id)
      : options.map(o => o.id);
    return Object.fromEntries(ordered.map((id, i) => [id, letterFor(i)]));
  }, [options, staticPollOptions]);

  // Render in authored order (A, B, C, …) — option shuffling already
  // happened at generation time, so the array we receive is the order
  // students saw. Sorting by count here would scramble A/B/C across the
  // x-axis and make the letter chips read out of order.
  const ordered = options;

  const maxCount = useMemo(() => Math.max(1, ...options.map(o => o.count)), [options]);

  const n = options.length || 1;
  const openOption = openOptionId
    ? options.find(o => o.id === openOptionId) ?? null
    : null;

  // All options with an authored explanation, in on-stage order. The
  // modal's manual prev/next arrow buttons walk this list so the host
  // can browse every explanation while the modal is open.
  const explainOptions = useMemo(() => options.filter(o => !!o.explanationRaw), [
    options,
  ]);
  // The clicker auto-walk opens the first CORRECT option that has an
  // explanation. With multiple correct options that have explanations,
  // we surface the first one in authored order — the rest are still
  // reachable via the modal's arrow nav.
  const firstCorrectExplainOption = useMemo(() => explainOptions.find(o => o.isCorrect), [
    explainOptions,
  ]);
  const hasCorrectExplanation = !!firstCorrectExplainOption;
  const currentExplainIndex = openOptionId
    ? explainOptions.findIndex(o => o.id === openOptionId)
    : -1;

  const openFirstMcExplanation = useCallback(() => {
    if (firstCorrectExplainOption) {
      setOpenOptionId(firstCorrectExplainOption.id);
    }
    // Latch even if nothing to open — keeps the contract symmetric with
    // strength review and lets the next press fall through to advance.
    // setHasMcExplanationBeenOpened writes through to storage too.
    setHasMcExplanationBeenOpened();
  }, [firstCorrectExplainOption, setHasMcExplanationBeenOpened]);
  // Manual navigation only — fires from the modal's arrow buttons and
  // arrow keys, never from the clicker forward action (the clicker
  // closes the modal instead).
  const nextMcExplanation = useCallback(() => {
    if (currentExplainIndex < 0) return;
    const next = explainOptions[currentExplainIndex + 1];
    if (next) setOpenOptionId(next.id);
  }, [currentExplainIndex, explainOptions]);
  const prevMcExplanation = useCallback(() => {
    if (currentExplainIndex <= 0) return;
    const prev = explainOptions[currentExplainIndex - 1];
    if (prev) setOpenOptionId(prev.id);
  }, [currentExplainIndex, explainOptions]);
  const closeMcExplanation = useCallback(() => {
    setOpenOptionId(null);
  }, []);

  // Register clicker state + handlers. Once the modal is open via the
  // clicker, the next forward press always closes it — single-shot
  // reveal of the correct answer's explanation. The modal's own arrow
  // buttons cover any browsing the host wants to do.
  useRegisterClickerLiveState(
    {
      isMcExplanationOpen: !!openOptionId,
      hasMcExplanationBeenOpened,
      mcHasExplanations: hasCorrectExplanation,
    },
    ['isMcExplanationOpen', 'hasMcExplanationBeenOpened', 'mcHasExplanations'],
  );
  useRegisterClickerHandlers(
    {
      openFirstMcExplanation,
      closeMcExplanation,
      // nextMcExplanation / prevMcExplanation are NOT fired by the
      // clicker's forward action (that closes the modal). They're
      // registered so the clicker hook can call them when the host
      // presses ArrowRight / ArrowLeft while the modal is open —
      // same pattern as browseNextResponse / browsePrevResponse for
      // the strength-review modal.
      nextMcExplanation,
      prevMcExplanation,
    },
    [
      'openFirstMcExplanation',
      'closeMcExplanation',
      'nextMcExplanation',
      'prevMcExplanation',
    ],
  );

  if (options.length === 0) return null;

  return (
    <>
      <ChartWrapper style={{ '--n': n } as React.CSSProperties}>
        <BarsGrid>
          {ordered.map((option, idx) => (
            <BarColumn
              key={option.id}
              option={option}
              maxCount={maxCount}
              isActive={openOptionId === option.id}
              onOpen={() => setOpenOptionId(option.id)}
              hasResults={hasResults}
              staggerIndex={idx}
            />
          ))}
          {!hasResults && (
            <LoaderOverlay aria-live="polite" aria-busy="true">
              <Dots>
                <Dot $delayMs={0} />
                <Dot $delayMs={160} />
                <Dot $delayMs={320} />
              </Dots>
              <LoaderText>{t('mcReveal.waiting', 'waiting for responses…')}</LoaderText>
            </LoaderOverlay>
          )}
        </BarsGrid>
        {/* Labels live in a sibling grid below the bars grid so all bar
            bottoms share one baseline. Multi-line label text grows
            downward inside this strip and never pushes the bar tops up. */}
        <LabelsGrid>
          {ordered.map(option => (
            <LabelColumn key={option.id}>
              <LabelLetterChip aria-hidden="true">
                {letterByOptionId[option.id]}
              </LabelLetterChip>
              <KeyText>
                <SlateRichText value={option.displayNameRaw} />
              </KeyText>
            </LabelColumn>
          ))}
        </LabelsGrid>
      </ChartWrapper>
      {openOption && (
        <RevealModal
          option={openOption}
          letter={letterByOptionId[openOption.id]}
          onClose={closeMcExplanation}
          onPrev={currentExplainIndex > 0 ? prevMcExplanation : undefined}
          onNext={
            currentExplainIndex >= 0 && currentExplainIndex < explainOptions.length - 1
              ? nextMcExplanation
              : undefined
          }
        />
      )}
    </>
  );
};

const BarColumn = ({
  option,
  maxCount,
  isActive,
  onOpen,
  hasResults,
  staggerIndex,
}: {
  option: RevealOption;
  maxCount: number;
  isActive: boolean;
  onOpen: () => void;
  hasResults: boolean;
  staggerIndex: number;
}) => {
  // Anticipating: all bars share a uniform min-height in the same neutral
  // color so the layout is already settled when results land.
  const heightPct = hasResults && maxCount > 0 ? (option.count / maxCount) * 100 : 0;
  const hasExplanation = !!option.explanationRaw && hasResults;
  // A 0-count bar collapses to the min-height stub. There's no real "bottom"
  // for the indicator/pill row to anchor to, so vertically center it within
  // the stub instead of letting it cling to the very bottom edge.
  const isZeroCount = hasResults && option.count === 0;

  return (
    <BarArea>
      <CountRow>
        {hasResults && option.isCorrect && (
          <CorrectMark
            style={{ animationDelay: `${staggerIndex * 50}ms` }}
            aria-label="Correct"
          >
            <CheckSvg />
          </CorrectMark>
        )}
        <Count
          $visible={hasResults}
          style={{
            animationDelay: `${staggerIndex * 50}ms`,
            ...(option.displayColor ? { color: option.displayColor } : {}),
          }}
        >
          {hasResults ? option.count : ''}
        </Count>
      </CountRow>
      {/* Hide the bar entirely while we wait for responses — the loader
          overlay + labels below carry the anticipating state. Once data
          arrives we mount the bar and let its height transition animate
          from 0% to the actual percentage. */}
      {hasResults && (
        <Bar
          $hasResults={hasResults}
          $correct={option.isCorrect}
          $active={isActive}
          $zeroCount={isZeroCount}
          style={{ '--h': `${heightPct}%` } as React.CSSProperties}
        >
          <BarGlyph $correct={option.isCorrect} />
          {hasExplanation && (
            <BarBottomRow>
              <ExplainPill
                type="button"
                onClick={onOpen}
                aria-haspopup="dialog"
                aria-label="See explanation"
                style={{ animationDelay: `${staggerIndex * 50 + 80}ms` }}
              >
                <ChatBubbleSvg />
              </ExplainPill>
            </BarBottomRow>
          )}
        </Bar>
      )}
    </BarArea>
  );
};

// ── Tiny Slate JSON renderer ──────────────────────────────────────────────
// Renders the paragraph + leaf structure used by `PollOptions.displayName`
// and `PollOptions.explanation` while preserving bold/italic/underline runs.

type SlateLeaf = {
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
};
// Inline void variable nodes produced by PromptTextEditor / the MC
// explanation popover. They wrap a single empty text child but the visible
// content is the `label` (e.g. "Student response", "Grade"). Without
// rendering these explicitly the reveal would silently drop them and leave
// gaps wherever the teacher inserted an AI variable.
type SlateVariable = {
  type: 'promptInput' | 'promptOutput' | 'livePromptInput';
  label?: string;
  children?: SlateLeaf[];
};
// Inline math authored in the option editor. The visible content is `value`
// (LaTeX); the children are an empty text leaf Slate requires on void nodes.
type SlateMathInline = {
  type: 'math-inline';
  value?: string;
  children?: { color?: string }[];
};
type SlateNode = {
  type?: string;
  children?: (SlateNode | SlateLeaf | SlateVariable | SlateMathInline)[];
};

const isLeaf = (n: unknown): n is SlateLeaf =>
  !!n && typeof (n as SlateLeaf).text === 'string';

const isVariable = (n: unknown): n is SlateVariable => {
  const t = (n as { type?: string }).type;
  return t === 'promptInput' || t === 'promptOutput' || t === 'livePromptInput';
};

const isMathInline = (n: unknown): n is SlateMathInline =>
  !!n && (n as { type?: string }).type === 'math-inline';

// Match the chip colors used in PromptTextEditor so a variable looks the
// same whether the teacher is authoring or the class is reading it.
const variableBg = (type: SlateVariable['type']) => {
  if (type === 'promptInput') return '#FFA55B';
  if (type === 'livePromptInput') return '#139982';
  return '#33A0DD';
};

const renderLeaf = (
  leaf: SlateLeaf,
  key: number,
  forceColor?: string,
): React.ReactNode => {
  let node: React.ReactNode = leaf.text ?? '';
  if (leaf.bold) node = <strong>{node}</strong>;
  if (leaf.italic) node = <em>{node}</em>;
  if (leaf.underline) node = <u>{node}</u>;
  // Preserve the leaf's authored color so the option's display name keeps
  // its palette color in the reveal — the green/red bar already carries
  // correctness, so the text color is free to express the teacher's choice.
  // The explanation modal passes forceColor='#000' so the dark card always
  // has high-contrast text regardless of how the teacher coloured the
  // option in the editor.
  const color = forceColor ?? leaf.color;
  return (
    <span key={key} style={color ? { color } : undefined}>
      {node}
    </span>
  );
};

const renderVariable = (v: SlateVariable, key: number): React.ReactNode => (
  <VariableChip key={key} $bg={variableBg(v.type)}>
    {v.label ?? ''}
  </VariableChip>
);

const renderMathInline = (
  node: SlateMathInline,
  key: number,
  forceColor?: string,
): React.ReactNode => {
  // Same colour rule as renderLeaf: the explanation modal pins every run
  // to black, and an authored child colour wins only when nothing forces it.
  // Otherwise inherit, so the label matches the surrounding KeyText colour.
  const authoredColor = node.children?.find(child => child.color)?.color;
  return (
    <LatexRenderer
      key={key}
      value={node.value || ''}
      display={false}
      color={forceColor ?? authoredColor}
    />
  );
};

const SlateRichText = ({
  value,
  forceColor,
}: {
  value: string;
  // When set, overrides every leaf's authored `color` so the rendered
  // text uses this exact CSS color. Used by the explanation modal to
  // pin everything to black on its beige card.
  forceColor?: string;
}) => {
  const parsed = useMemo(() => {
    if (!value) return [] as SlateNode[];
    try {
      return parseStringToSlateObject(value) as SlateNode[];
    } catch {
      return [] as SlateNode[];
    }
  }, [value]);
  if (parsed.length === 0) return null;
  return (
    <>
      {parsed.map((block, i) => (
        <span key={i}>
          {(block.children || []).map((child, j) => {
            if (isVariable(child)) return renderVariable(child, j);
            if (isMathInline(child)) return renderMathInline(child, j, forceColor);
            if (isLeaf(child)) return renderLeaf(child, j, forceColor);
            return null;
          })}
        </span>
      ))}
    </>
  );
};

const VariableChip = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.05em 0.4em;
  margin: 0 0.15em;
  border-radius: 0.3em;
  background: ${({ $bg }) => $bg};
  color: #fff;
  font-weight: 800;
  font-size: 0.9em;
  vertical-align: baseline;
`;

const RevealModal = ({
  option,
  letter,
  onClose,
  onPrev,
  onNext,
}: {
  option: RevealOption;
  letter: string;
  onClose: () => void;
  // Optional prev/next handlers — undefined when at the first/last
  // explanation. When both are undefined the modal renders the close
  // button on its own.
  onPrev?: () => void;
  onNext?: () => void;
}) => {
  const { t } = useTranslate();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard
        $correct={option.isCorrect}
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <ModalClose onClick={onClose} aria-label="Close">
          <CrossSvg />
        </ModalClose>
        <ModalHero>
          <StatusPill $correct={option.isCorrect}>
            <StatusDot>{option.isCorrect ? <CheckSvg /> : <CrossSvg />}</StatusDot>
            {option.isCorrect
              ? t('mcReveal.correct', 'Correct')
              : t('mcReveal.incorrect', 'Incorrect')}
          </StatusPill>
          <ModalOptionText>
            <ModalLetter aria-hidden="true">{letter}</ModalLetter>
            <span>
              <SlateRichText
                value={option.displayNameRaw}
                forceColor={theme.colors.black}
              />
            </span>
          </ModalOptionText>
        </ModalHero>
        <ModalBody>
          <ModalEyebrow>
            <EyebrowIcon>
              <ChatBubbleSvg />
            </EyebrowIcon>
            {t('mcReveal.explanation', 'Explanation')}
          </ModalEyebrow>
          <ModalExplanation>
            <SlateRichText
              value={option.explanationRaw}
              forceColor={theme.colors.black}
            />
          </ModalExplanation>
        </ModalBody>
        <ModalFoot>
          <ModalNavButton
            type="button"
            onClick={onPrev}
            disabled={!onPrev}
            aria-label="Previous explanation"
          >
            <ArrowLeftSvg />
          </ModalNavButton>
          <ModalCta onClick={onClose}>{t('mcReveal.gotIt', 'Got it')}</ModalCta>
          <ModalNavButton
            type="button"
            onClick={onNext}
            disabled={!onNext}
            aria-label="Next explanation"
          >
            <ArrowRightSvg />
          </ModalNavButton>
        </ModalFoot>
      </ModalCard>
    </ModalOverlay>
  );
};

// ── Chart / bars ──────────────────────────────────────────────────────────
// Renders inside `PollDataContainer` (positioned bottom 25% of the 1920×1080
// stage). Scaling math comes from the design and is keyed off `--n` (the
// number of options, 2–10) so 10 narrow bars still read.

// Bars and labels live in separate grids stacked vertically. All bar
// bottoms share the BarsGrid baseline regardless of how many lines each
// label below wraps to, so wider/longer answer text grows downward in
// LabelsGrid without nudging the bars upward.
const ChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const BarsGrid = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(var(--n, 4), minmax(0, 1fr));
  gap: clamp(10px, calc(40px - var(--n, 4) * 3px), 32px);
  align-items: end;
  position: relative;
`;

const LabelsGrid = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(var(--n, 4), minmax(0, 1fr));
  gap: clamp(10px, calc(40px - var(--n, 4) * 3px), 32px);
  align-items: start;
  padding-top: 14px;
`;

// Answer text sits centered below each bar. The letter chip lives
// inside the bar (BarLetterChip) so the label strip only carries text.
const LabelColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 0;
`;

const BarArea = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding-bottom: 6px;
  position: relative;
`;

const rowIn = keyframes`
  from { transform: translateY(8px); opacity: 0; }
`;

// Holds the green Correct chip + Count number side by side, above the bar.
const CountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const Count = styled.div<{ $visible: boolean }>`
  font-size: clamp(28px, calc(78px - var(--n, 4) * 5px), 64px);
  font-weight: 900;
  color: ${theme.colors.black};
  letter-spacing: -0.02em;
  line-height: 1;
  min-height: 1em;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  ${({ $visible }) =>
    $visible &&
    css`
      animation: ${rowIn} ${ROW_IN_MS}ms ${EASE} backwards;
    `};
`;

// Correct indicator that sits to the left of the response count. Sized to
// scale loosely with the count font so the pair reads as a balanced unit.
const CorrectMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: clamp(24px, calc(58px - var(--n, 4) * 3px), 44px);
  height: clamp(24px, calc(58px - var(--n, 4) * 3px), 44px);
  border-radius: 999px;
  background: linear-gradient(180deg, #19b596 0%, #117d67 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(13, 126, 100, 0.35);
  flex-shrink: 0;
  animation: ${rowIn} ${ROW_IN_MS}ms ${EASE} backwards;
  svg {
    width: clamp(14px, calc(26px - var(--n, 4) * 1.2px), 22px);
    height: auto;
  }
`;

const Bar = styled.div<{
  $correct: boolean;
  $active: boolean;
  $hasResults: boolean;
  $zeroCount: boolean;
}>`
  width: 100%;
  max-width: 240px;
  height: var(--h, 0%);
  /* While anticipating, keep a chunky uniform floor so all bars look like
     bars even at 0%. Once results arrive we drop the floor to ~56px (just
     enough for the white indicator badge) so small counts like 5 vs 3 stay
     visually distinct instead of both being clamped to the same height. */
  min-height: ${({ $hasResults }) => ($hasResults ? '56px' : '132px')};
  border-radius: 18px 18px 0 0;
  position: relative;
  display: flex;
  flex-direction: column;
  /* Pin the indicator + explain-pill row to the bottom-left of the bar.
     Only horizontal + bottom padding so small bars still fit the 40px
     indicator without clipping. 0-count bars collapse to the min-height
     stub, so we vertically center the row inside the stub instead — the
     indicator sits in the middle of the tiny bar rather than hugging the
     bottom edge with no visual context below it. */
  align-items: flex-start;
  justify-content: ${({ $zeroCount }) => ($zeroCount ? 'center' : 'flex-end')};
  padding: ${({ $zeroCount }) => ($zeroCount ? '0 12px' : '0 12px 12px')};
  background: ${({ $correct, $hasResults }) =>
    !$hasResults
      ? 'linear-gradient(180deg, rgba(40,47,51,0.18) 0%, rgba(40,47,51,0.12) 100%)'
      : $correct
      ? 'linear-gradient(180deg, #19B596 0%, #117D67 100%)'
      : 'linear-gradient(180deg, #E45B51 0%, #B53A33 100%)'};
  box-shadow: inset 0 4px 0 rgba(255, 255, 255, 0.14), inset 0 -3px 0 rgba(0, 0, 0, 0.06);
  transition: height ${BAR_FILL_MS}ms ${EASE}, background 0.6s ${EASE};
  overflow: hidden;
  ${({ $active }) =>
    $active && `outline: 3px solid ${theme.colors.black}; outline-offset: 4px;`}
`;

const BarBottomRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  // Above the optional CorrectWatermark so the explain pill stays clickable.
  position: relative;
  z-index: 1;
`;

const ExplainPill = styled.button`
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border: none;
  color: ${theme.colors.black};
  // Icon-only — the indicator beside the pill conveys correctness and the
  // chat-bubble icon advertises "tap for more". Adding a "See explanation"
  // label crowded the bottom row, so we drop it entirely.
  padding: 7px;
  border-radius: 999px;
  font-family: inherit;
  font-weight: 900;
  font-size: clamp(11px, calc(16px - var(--n, 4) * 0.3px), 14px);
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition: background 0.15s ease, transform 0.15s ease;
  animation: ${rowIn} ${ROW_IN_MS}ms ${EASE} backwards;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  svg {
    flex-shrink: 0;
    width: clamp(11px, calc(16px - var(--n, 4) * 0.3px), 14px);
    height: auto;
  }
  &:hover {
    background: #fff;
    transform: translateY(-1px);
  }
`;

// Small circular letter chip that sits above each answer in the key strip.
// The letter is stable per option (A/B/C/…) even as bars sort by count.
// Oversized decorative glyph (check or cross) bleeding off the
// bottom-right corner of the bar at 6% white opacity — same treatment
// as the student-facing answer card so host + student stages share the
// same visual language. Sits behind everything (z-index: 0).
const BarGlyphContainer = styled.div<{ $correct: boolean }>`
  position: absolute;
  width: 110%;
  aspect-ratio: 1;
  color: rgba(255, 255, 255, 0.08);
  pointer-events: none;
  z-index: 0;
  /* Incorrect glyph (cross) centres in the bar so it reads as a stamp
     even when the bar is short. Correct glyph (check) stays anchored
     to the bottom-right corner of the (typically taller) green bar. */
  ${({ $correct }) =>
    $correct
      ? css`
          right: -5%;
          bottom: -28%;
        `
      : css`
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
        `};
  svg {
    width: 100%;
    height: 100%;
  }
`;
const BarGlyph = ({ $correct }: { $correct: boolean }) => (
  <BarGlyphContainer $correct={$correct} aria-hidden="true">
    {$correct ? (
      <svg
        viewBox="0 0 511.985 511.985"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M500.088,83.681c-15.841-15.862-41.564-15.852-57.426,0L184.205,342.148L69.332,227.276c-15.862-15.862-41.574-15.862-57.436,0c-15.862,15.862-15.862,41.574,0,57.436l143.585,143.585c7.926,7.926,18.319,11.899,28.713,11.899c10.394,0,20.797-3.963,28.723-11.899l287.171-287.181C515.95,125.265,515.95,99.542,500.088,83.681z"
        />
      </svg>
    ) : (
      // Chunky X tuned to match the check's visual weight — same 512×512
      // canvas, ~50px stroke. Without this the thin 24×24 default rendered
      // as a wispy line inside the bar while the check filled it.
      <svg
        viewBox="0 0 511.985 511.985"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M437.5,74.485c-15.862-15.862-41.574-15.862-57.436,0L255.993,198.557L131.921,74.485c-15.862-15.862-41.574-15.862-57.436,0c-15.862,15.862-15.862,41.574,0,57.436l124.072,124.072L74.485,380.064c-15.862,15.862-15.862,41.574,0,57.436c15.862,15.862,41.574,15.862,57.436,0l124.072-124.072L380.064,437.5c15.862,15.862,41.574,15.862,57.436,0c15.862-15.862,15.862-41.574,0-57.436L313.429,255.993L437.5,131.921C453.362,116.059,453.362,90.347,437.5,74.485z"
        />
      </svg>
    )}
  </BarGlyphContainer>
);

// Letter chip sits between the bar (above) and the answer text (below)
// inside LabelColumn. Anchored on a dark background fill so it reads
// crisply on the beige stage.
const LabelLetterChip = styled.span`
  width: clamp(28px, calc(48px - var(--n, 4) * 2px), 40px);
  height: clamp(28px, calc(48px - var(--n, 4) * 2px), 40px);
  border-radius: 999px;
  background: ${theme.colors.black};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(14px, calc(22px - var(--n, 4) * 0.6px), 18px);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1;
  flex-shrink: 0;
  user-select: none;
  margin-bottom: 6px;
`;

const KeyText = styled.span`
  font-size: clamp(13px, calc(34px - var(--n, 4) * 2px), 28px);
  font-weight: 700;
  color: ${theme.colors.black};
  letter-spacing: -0.005em;
  line-height: 1.18;
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  padding: 0 4px;
  strong {
    font-weight: 900;
  }
`;

// ── Anticipation loader ────────────────────────────────────────────────────

const loaderDotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0);  opacity: 0.45; }
  40%          { transform: translateY(-10px); opacity: 1; }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  pointer-events: none;
`;

const Dots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 14px;
`;

const Dot = styled.span<{ $delayMs: number }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${theme.colors.orange};
  animation: ${loaderDotBounce} 1.4s ${EASE} infinite;
  animation-delay: ${({ $delayMs }) => $delayMs}ms;
`;

const LoaderText = styled.span`
  font-size: clamp(18px, 2vw, 28px);
  font-weight: 900;
  color: ${theme.colors.black};
  opacity: 0.7;
  letter-spacing: -0.01em;
`;

// ── Modal ─────────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 22, 25, 0.55);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  backdrop-filter: blur(2px);
  animation: mc-overlay-in 0.18s ease-out;
  @keyframes mc-overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalCard = styled.div<{ $correct: boolean }>`
  width: min(880px, 88%);
  background: #fbf3e7;
  border-radius: 24px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.34), 0 8px 20px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  position: relative;
  animation: mc-modal-in 0.26s ${EASE};
  @keyframes mc-modal-in {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: rgba(40, 47, 51, 0.08);
  color: ${theme.colors.black};
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  transition: background 0.15s ease;
  svg {
    width: 14px;
    height: 14px;
  }
  &:hover {
    background: rgba(40, 47, 51, 0.16);
  }
`;

const ModalHero = styled.div`
  position: relative;
  padding: 40px 56px 24px;
  color: ${theme.colors.black};
`;

const StatusPill = styled.div<{ $correct: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  border-radius: 999px;
  padding: 8px 18px 8px 8px;
  font-size: 14px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  background: ${({ $correct }) => ($correct ? '#15A082' : '#D84C42')};
`;

const StatusDot = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ModalOptionText = styled.div`
  margin-top: 18px;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.005em;
  line-height: 1.2;
  color: rgba(40, 47, 51, 0.7);
  text-wrap: balance;
  display: flex;
  align-items: center;
  gap: 12px;
  strong {
    font-weight: 900;
  }
`;

// Letter chip beside the (now-secondary) answer text. Scaled down to
// match the smaller answer line so the chip doesn't dwarf the label.
const ModalLetter = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: ${theme.colors.black};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 900;
  line-height: 1;
  flex-shrink: 0;
  user-select: none;
`;

const ModalBody = styled.div`
  padding: 26px 56px 36px;
  border-top: 1px solid rgba(40, 47, 51, 0.08);
  margin-top: 14px;
`;

const ModalEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${theme.colors.orangeDark};
  margin-bottom: 14px;
`;

const EyebrowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: rgba(249, 151, 70, 0.18);
  color: ${theme.colors.orangeDark};
  border-radius: 999px;
  svg {
    width: 12px;
    height: 12px;
  }
`;

const ModalExplanation = styled.p`
  font-size: 44px;
  font-weight: 800;
  line-height: 1.25;
  color: ${theme.colors.black};
  margin: 0;
  text-wrap: pretty;
  strong {
    font-weight: 900;
  }
`;

const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 56px 36px;
`;

const ModalCta = styled.button`
  background: ${theme.colors.orange};
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  font-family: inherit;
  font-weight: 900;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(249, 151, 70, 0.32);
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    background: ${theme.colors.orangeDark};
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(249, 151, 70, 0.4);
  }
`;

// Prev/next nav buttons flanking the central Got-it CTA. Disabled when
// at the start/end of the explanation walk.
const ModalNavButton = styled.button`
  background: rgba(40, 47, 51, 0.06);
  color: ${theme.colors.black};
  border: none;
  border-radius: 999px;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, opacity 0.15s ease;
  svg {
    width: 18px;
    height: 18px;
  }
  &:hover:not(:disabled) {
    background: rgba(40, 47, 51, 0.12);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export default MultipleChoiceHostReveal;
